import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Animated,
  Platform,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { spacing } from '../../../shared/theme/spacing'
import { typography } from '../../../shared/theme/typography';
import { ticketColors } from '../../../shared/theme/ticketColors';
import { matchesSearchAny } from '../../../shared/format/searchText';
import { hapticTap } from '../../../shared/ui/haptics';
import { SoftPressable } from '../../../shared/ui/SoftPressable';
import { ModuleEmptyState } from '../../../shared/ui/ModuleEmptyState';
import type { BusinessTypeCode, BusinessTypeListItem } from '../../../shell/session/domain/businessProfile';
import type { OnboardingLayout } from './useOnboardingLayout';
import { OTHER_RUBRO_PICKER_CODE } from '../../../onboarding/application/otherRubroFlow';
import type { OnboardingPickerItem } from '../../../onboarding/application/onboardingPickerService';

type PickerRow = OnboardingPickerItem | (BusinessTypeListItem & {
  picker_key?: string;
  vertical_flavor?: string | null;
});

type Props = {
  businessTypes: readonly PickerRow[];
  selectedCode?: BusinessTypeCode;
  /** Prefer picker_key when flavors share the same ERP code. */
  selectedPickerKey?: string;
  onSelect: (item: OnboardingPickerItem) => void;
  layout: OnboardingLayout;
  loading?: boolean;
  disabled?: boolean;
  /** Retry del fetch — se muestra en el estado vacío/error del grid. */
  onRetry?: () => void;
  excludeCode?: BusinessTypeCode | null;
  currentCode?: BusinessTypeCode | null;
  searchable?: boolean;
  compact?: boolean;
};

const VISIBLE_COUNT_MOBILE = 5;
const VISIBLE_COUNT_DESKTOP = 8;
/** Altura aprox. del search sticky; las secciones se pegan debajo. */
const SEARCH_STICKY_OFFSET = 52;
const SKELETON_ROWS = 4;

function asPickerItem(row: PickerRow): OnboardingPickerItem {
  return {
    code: row.code,
    display_name: row.display_name,
    description: row.description,
    sort_order: row.sort_order,
    onboarding_pack_code: row.onboarding_pack_code,
    onboarding_picker_group: row.onboarding_picker_group,
    picker_key: row.picker_key ?? row.code,
    vertical_flavor: row.vertical_flavor ?? null,
    picker_icon: row.picker_icon ?? null,
    picker_keywords: row.picker_keywords ?? [],
  };
}

/** Placeholder de tiles durante la carga (misma forma que la grilla real). */
function PickerTilesSkeleton({ columns, gap }: { columns: number; gap: number }) {
  const count = columns * SKELETON_ROWS;
  const widthPct = columns === 3 ? '32%' : '48.5%';
  const pulse = useRef(new Animated.Value(0.45)).current;
  useEffect(() => {
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(pulse, { toValue: 1, duration: 650, useNativeDriver: true }),
        Animated.timing(pulse, { toValue: 0.45, duration: 650, useNativeDriver: true }),
      ]),
    );
    loop.start();
    return () => loop.stop();
  }, [pulse]);
  return (
    <Animated.View
      style={[styles.tileGrid, { gap, opacity: pulse }]}
      testID="business-type-picker-skeleton"
    >
      {Array.from({ length: count }, (_, i) => (
        <View key={i} style={[styles.skelTile, { flexBasis: widthPct, maxWidth: widthPct }]} />
      ))}
    </Animated.View>
  );
}

export function BusinessTypePickerGrid({
  businessTypes,
  selectedCode,
  selectedPickerKey,
  onSelect,
  layout,
  loading = false,
  disabled = false,
  onRetry,
  excludeCode = null,
  currentCode = null,
  searchable: searchableProp,
  compact = false,
}: Props) {
  const [expanded, setExpanded] = useState(false);
  const [query, setQuery] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [focusedKey, setFocusedKey] = useState<string | null>(null);
  const searchInputRef = useRef<TextInput>(null);

  const visibleTypes = useMemo(
    () =>
      (excludeCode ? businessTypes.filter((t) => t.code !== excludeCode) : businessTypes).map(
        asPickerItem,
      ),
    [businessTypes, excludeCode],
  );

  const searchable = searchableProp ?? visibleTypes.length > VISIBLE_COUNT_DESKTOP;
  const hasQuery = query.trim().length > 0;
  const gridColumns = layout.typeGridColumns;
  const gridGap = layout.typeGridGap;
  /** fondo detrás de labels/search sticky (web). */
  const stickyBg = layout.useSplitLayout ? ticketColors.paper : ticketColors.bg;
  const tileBasisPct = gridColumns === 3 ? '31.5%' : '47%';
  const tileMaxPct = gridColumns === 3 ? '32.5%' : '48.8%';

  // Desktop: Ctrl/Cmd+K enfoca el search (D3).
  useEffect(() => {
    if (Platform.OS !== 'web' || !searchable) return;
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [searchable]);

  const filteredTypes = useMemo(() => {
    if (!hasQuery) return visibleTypes;
    return visibleTypes.filter((t) =>
      matchesSearchAny(
        [
          t.display_name,
          t.description,
          t.code,
          t.onboarding_picker_group,
          ...(t.picker_keywords ?? []),
        ],
        query,
      ),
    );
  }, [visibleTypes, hasQuery, query]);

  const { displaySections, flatDisplayTypes, hasExtra } = useMemo(() => {
    const source = searchable ? filteredTypes : visibleTypes;
    const otrosType = source.find((t) => t.code === OTHER_RUBRO_PICKER_CODE) ?? null;
    const regularTypes = source.filter((t) => t.code !== OTHER_RUBRO_PICKER_CODE);

    if (searchable) {
      const ordered = otrosType ? [...regularTypes, otrosType] : regularTypes;
      if (hasQuery) {
        return {
          displaySections: [{ group: null as string | null, types: ordered }],
          flatDisplayTypes: ordered,
          hasExtra: false,
        };
      }
      const byGroup = new Map<string | null, OnboardingPickerItem[]>();
      for (const type of ordered) {
        const key = type.onboarding_picker_group ?? null;
        const bucket = byGroup.get(key) ?? [];
        bucket.push(type);
        byGroup.set(key, bucket);
      }
      const sections: { group: string | null; types: OnboardingPickerItem[] }[] = [];
      for (const type of ordered) {
        const key = type.onboarding_picker_group ?? null;
        if (sections.some((s) => s.group === key)) continue;
        sections.push({ group: key, types: byGroup.get(key) ?? [] });
      }
      return { displaySections: sections, flatDisplayTypes: ordered, hasExtra: false };
    }

    const visibleCount = layout.isWide ? VISIBLE_COUNT_DESKTOP : VISIBLE_COUNT_MOBILE;
    const regularSlots = otrosType ? Math.max(0, visibleCount - 1) : visibleCount;
    const extra = regularTypes.length > regularSlots;
    const primaryRegular = expanded ? regularTypes : regularTypes.slice(0, regularSlots);
    const ordered = otrosType ? [...primaryRegular, otrosType] : primaryRegular;
    return {
      displaySections: [{ group: null, types: ordered }],
      flatDisplayTypes: ordered,
      hasExtra: extra,
    };
  }, [visibleTypes, filteredTypes, layout.isWide, expanded, searchable, hasQuery]);

  /** Otros sin filtrar — para el CTA del empty-search. */
  const otrosItem = useMemo(
    () => visibleTypes.find((t) => t.code === OTHER_RUBRO_PICKER_CODE) ?? null,
    [visibleTypes],
  );

  if (loading) {
    return (
      <View style={styles.root}>
        {searchable ? <View style={styles.skelSearch} /> : null}
        <PickerTilesSkeleton columns={gridColumns} gap={gridGap} />
      </View>
    );
  }

  if (visibleTypes.length === 0) {
    return (
      <ModuleEmptyState
        title="No pudimos cargar los rubros"
        subtitle="Revisá tu conexión y probá de nuevo."
        primaryAction={onRetry ? { label: 'Reintentar', onPress: onRetry } : undefined}
        testID="business-type-picker-empty"
      />
    );
  }

  const isSelected = (type: OnboardingPickerItem) => {
    if (selectedPickerKey) return type.picker_key === selectedPickerKey;
    return type.code === selectedCode;
  };

  const renderTile = (type: OnboardingPickerItem) => {
    const selected = isSelected(type);
    const isCurrent = currentCode != null && type.code === currentCode && !type.vertical_flavor;
    const isOtros = type.code === OTHER_RUBRO_PICKER_CODE;
    const webFocused = Platform.OS === 'web' && focusedKey === type.picker_key;
    return (
      <SoftPressable
        key={type.picker_key}
        style={[
          styles.tile,
          compact && styles.tileCompact,
          !isOtros
            ? { flexGrow: 1, flexShrink: 1, flexBasis: tileBasisPct, maxWidth: tileMaxPct }
            : styles.tileFull,
          isOtros && styles.tileOtros,
          selected && styles.tileSelected,
          isCurrent && styles.tileNow,
          webFocused && styles.tileFocused,
        ]}
        onPress={() => {
          hapticTap();
          onSelect(type);
        }}
        onFocus={() => setFocusedKey(type.picker_key)}
        onBlur={() => setFocusedKey((k) => (k === type.picker_key ? null : k))}
        disabled={disabled || isCurrent}
        accessibilityRole="button"
        accessibilityState={{ selected, disabled: disabled || isCurrent }}
        accessibilityLabel={isCurrent ? `${type.display_name} (ahora)` : type.display_name}
        testID={`business-type-${type.picker_key}`}
      >
        {type.picker_icon ? (
          <View
            style={[
              styles.tileIconBox,
              {
                width: layout.typeIconBoxSize,
                height: layout.typeIconBoxSize,
                borderRadius: 9,
              },
              selected && styles.tileIconBoxSelected,
            ]}
          >
            <Text style={{ fontSize: layout.typeIconGlyphSize }}>{type.picker_icon}</Text>
          </View>
        ) : null}
        <View style={styles.tileText}>
          <View style={styles.tileNameRow}>
            <Text
              style={[styles.tileName, selected && styles.tileNameSelected]}
              numberOfLines={2}
            >
              {type.display_name}
            </Text>
            {isCurrent ? <Text style={styles.nowBadge}>ahora</Text> : null}
          </View>
          {type.description ? (
            <Text style={styles.tileDesc} numberOfLines={2}>
              {type.description}
            </Text>
          ) : null}
        </View>
        {selected ? (
          <View style={styles.tileCheck} pointerEvents="none">
            <Ionicons name="checkmark" size={12} color={ticketColors.goldInk} />
          </View>
        ) : null}
      </SoftPressable>
    );
  };

  const renderSectionTypes = (types: OnboardingPickerItem[]) => {
    const regular = types.filter((t) => t.code !== OTHER_RUBRO_PICKER_CODE);
    const otros = types.filter((t) => t.code === OTHER_RUBRO_PICKER_CODE);
    return (
      <>
        <View style={[styles.tileGrid, { gap: gridGap }]}>{regular.map(renderTile)}</View>
        {otros.map(renderTile)}
      </>
    );
  };

  return (
    <View style={styles.root}>
      {searchable ? (
        <View
          style={
            Platform.OS === 'web'
              ? [styles.searchStickyWrap, { backgroundColor: stickyBg }]
              : undefined
          }
        >
          <View style={[styles.search, searchFocused ? styles.searchFocused : null]}>
            <Ionicons name="search-outline" size={16} color={ticketColors.ink3} />
            <TextInput
              ref={searchInputRef}
              value={query}
              onChangeText={setQuery}
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setSearchFocused(false)}
              placeholder="Ej. kiosco, pizzería, vinoteca…"
              placeholderTextColor={ticketColors.ink3}
              style={styles.searchInput}
              editable={!disabled}
              autoCorrect={false}
              autoCapitalize="none"
              accessibilityLabel="Buscá tu rubro"
              testID="business-type-search"
            />
            {query.length > 0 ? (
              <TouchableOpacity
                onPress={() => setQuery('')}
                accessibilityRole="button"
                accessibilityLabel="Limpiar búsqueda"
                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              >
                <Ionicons name="close-circle" size={18} color={ticketColors.ink3} />
              </TouchableOpacity>
            ) : Platform.OS === 'web' ? (
              <View style={styles.kbdHint}>
                <Text style={styles.kbdText}>Ctrl K</Text>
              </View>
            ) : null}
          </View>
        </View>
      ) : null}

      {searchable && flatDisplayTypes.length === 0 ? (
        <View testID="business-type-picker-no-results">
          <ModuleEmptyState
            showMascot
            title={`Nada con "${query.trim()}"`}
            subtitle="Probá otra palabra — o elegí Otros y armamos tu rubro a medida."
          />
          {otrosItem ? (
            <SoftPressable
              style={styles.otrosGhostLink}
              onPress={() => {
                hapticTap();
                onSelect(otrosItem);
              }}
              accessibilityRole="button"
              accessibilityLabel="Otros — contanos qué vendés"
            >
              <Text style={styles.otrosGhostLinkLabel}>➕ Otros — contanos qué vendés</Text>
            </SoftPressable>
          ) : null}
        </View>
      ) : (
        <View style={styles.list}>
          {displaySections.map(({ group, types }) => (
            <View key={group ?? 'ungrouped'} style={styles.groupBlock}>
              {group && !hasQuery ? (
                <Text
                  style={[
                    styles.groupLabel,
                    Platform.OS === 'web' && searchable
                      ? [styles.groupLabelSticky, { backgroundColor: stickyBg }]
                      : null,
                  ]}
                >
                  {group}
                </Text>
              ) : null}
              {renderSectionTypes(types)}
            </View>
          ))}
        </View>
      )}

      {hasExtra ? (
        <TouchableOpacity
          style={styles.moreBtn}
          onPress={() => setExpanded((v) => !v)}
          accessibilityRole="button"
          accessibilityLabel={expanded ? 'Ver menos rubros' : 'Ver más rubros'}
        >
          <Text style={styles.moreBtnLabel}>{expanded ? 'Ver menos' : 'Ver más rubros'}</Text>
          <Ionicons
            name={expanded ? 'chevron-up-outline' : 'chevron-down-outline'}
            size={16}
            color={ticketColors.ink2}
          />
        </TouchableOpacity>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  root: { gap: spacing.sm },
  list: { gap: spacing.sm },
  groupBlock: { gap: 6 },
  groupLabel: {
    ...typography.caption,
    color: ticketColors.brandPrimary,
    fontWeight: '700',
    fontSize: 11,
    letterSpacing: 0.9,
    textTransform: 'uppercase',
    paddingHorizontal: 2,
    paddingTop: 8,
    paddingBottom: 4,
  },
  groupLabelSticky: {
    position: 'sticky' as any,
    top: SEARCH_STICKY_OFFSET,
    zIndex: 1,
  },
  search: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    borderWidth: 1.5,
    borderColor: ticketColors.rule,
    borderRadius: 10,
    /** Proto: --counter (#EAF3F0) en reposo, paper en focus. */
    backgroundColor: ticketColors.counter,
    paddingHorizontal: spacing.md,
    paddingVertical: 10,
    minHeight: 44,
  },
  searchFocused: {
    borderColor: ticketColors.brandPrimary,
    backgroundColor: ticketColors.paper,
    boxShadow: '0 0 0 3px rgba(15, 118, 110, 0.14)',
  } as any,
  /** Wrapper sticky web (proto .search-wrap): el fondo opaco va ACÁ, no en el input. */
  searchStickyWrap: {
    position: 'sticky' as any,
    top: 0,
    zIndex: 2,
    paddingBottom: 2,
  },
  searchInput: {
    flex: 1,
    ...typography.body,
    color: ticketColors.ink,
    paddingVertical: 2,
    fontSize: 15,
    outlineStyle: 'none' as any,
  },
  kbdHint: {
    borderWidth: 1,
    borderColor: ticketColors.rule,
    borderBottomWidth: 2,
    borderRadius: 6,
    paddingHorizontal: 6,
    paddingVertical: 1,
    backgroundColor: ticketColors.paper,
  },
  kbdText: { fontSize: 11, fontWeight: '600', color: ticketColors.ink3 },
  tileGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  tile: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    borderWidth: 1.5,
    borderColor: ticketColors.rule,
    borderRadius: 10,
    backgroundColor: ticketColors.paper,
    paddingVertical: 12,
    paddingHorizontal: 12,
    minHeight: 46,
    position: 'relative',
  },
  tileFull: {
    width: '100%',
  },
  tileCompact: {
    paddingVertical: 10,
    paddingHorizontal: 10,
    minHeight: 40,
  },
  tileOtros: {
    borderStyle: 'dashed',
    borderColor: ticketColors.brandPrimary,
    marginTop: 4,
  },
  /** Proto: .ghost-link — CTA dashed brand hacia Otros en empty-search. */
  otrosGhostLink: {
    borderWidth: 1.5,
    borderStyle: 'dashed',
    borderColor: ticketColors.brandPrimary,
    backgroundColor: ticketColors.paper,
    borderRadius: 10,
    paddingVertical: 12,
    paddingHorizontal: spacing.md,
    alignItems: 'center',
    marginTop: spacing.sm,
  },
  otrosGhostLinkLabel: {
    fontSize: 14,
    fontWeight: '700',
    color: ticketColors.brandPrimary,
  },
  tileNow: { opacity: 0.55 },
  tileSelected: {
    borderColor: ticketColors.brandPrimary,
    backgroundColor: ticketColors.brandSoft,
    borderWidth: 2,
  },
  /** Focus ring web (teclado). Solo aplica con borde visible. */
  tileFocused: {
    borderColor: ticketColors.brandPrimary,
    boxShadow: '0 0 0 3px rgba(15, 118, 110, 0.18)',
  } as any,
  tileIconBox: {
    backgroundColor: ticketColors.counter,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  tileIconBoxSelected: { backgroundColor: ticketColors.paper },
  tileText: { flex: 1, minWidth: 0, gap: 2 },
  tileNameRow: { flexDirection: 'row', alignItems: 'center', gap: 6, flexWrap: 'wrap' },
  nowBadge: {
    fontSize: 10,
    fontWeight: '700',
    letterSpacing: 0.4,
    textTransform: 'uppercase',
    color: ticketColors.ink3,
    backgroundColor: ticketColors.paper,
    borderRadius: 99,
    paddingHorizontal: 6,
    paddingVertical: 1,
    overflow: 'hidden',
  },
  tileName: {
    ...typography.body,
    color: ticketColors.ink,
    fontWeight: '700',
    fontSize: 13,
    letterSpacing: -0.1,
  },
  tileNameSelected: { color: ticketColors.brandPrimary },
  tileDesc: {
    ...typography.caption,
    color: ticketColors.ink2,
    fontSize: 11,
    lineHeight: 14,
    fontWeight: '400',
  },
  /** Check flotante sobre la esquina del tile seleccionado. */
  tileCheck: {
    position: 'absolute',
    top: -7,
    right: -7,
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: ticketColors.brandPrimary,
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 1px 4px rgba(15, 118, 110, 0.4)',
  } as any,
  skelSearch: {
    height: 44,
    borderRadius: 10,
    backgroundColor: ticketColors.paper2,
  },
  skelTile: {
    height: 56,
    borderRadius: 10,
    backgroundColor: ticketColors.paper2,
  },
  moreBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.xs,
    paddingVertical: spacing.sm,
  },
  moreBtnLabel: { ...typography.body, color: ticketColors.ink2, fontWeight: '600', fontSize: 14 },
});
